// src/features/MediaLibrary/MediaSelector.js
import React, { useState } from 'react';
import MediaBrowser from './MediaBrowser';
import AudioBrowser from './AudioBrowser';
import MediaUpload from './MediaUpload';
import AudioUpload from './AudioUpload';
import './MediaSelector.css'; // Reuse existing styles

/**
 * Media Selector for both images and audio
 * 
 * @param {Object} props
 * @param {string} props.mediaType - 'image', 'audio', or 'both'
 * @param {string} props.unitId - Current unit ID for tracking usage
 * @param {Function} props.onSelectMedia - Callback when media is selected
 * @param {Function} props.onCancel - Callback when selection is canceled
 * @param {boolean} props.showFullForm - Show full upload form (admin) vs simple (units)
 */
const MediaSelector = ({
  mediaType = 'image',
  unitId,
  onSelectMedia,
  onCancel,
  showFullForm = false
}) => {
  const [activeTab, setActiveTab] = useState('upload'); // UPLOAD FIRST everywhere!
  const [currentMediaType, setCurrentMediaType] = useState(
    mediaType === 'both' ? 'image' : mediaType
  ); // For 'both' mode, start with images
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [error, setError] = useState(null);

  const showMediaTypeTabs = mediaType === 'both';
  const isImage = currentMediaType === 'image';
  // Handle media selection from browser
  const handleMediaSelect = (media) => {
    setSelectedMedia(media);
  };

  // Handle confirmation of selection
  const handleConfirmSelection = () => {
    if (selectedMedia && onSelectMedia) {
      onSelectMedia(selectedMedia);
    } else {
      setError(`Please select ${isImage ? 'an image' : 'an audio file'} first`);
    }
  };

  // Handle upload completion
  const handleUploadComplete = (newMedia) => {
    // Auto-select the newly uploaded media
    setSelectedMedia(newMedia);
    
    // Switch to browse tab to show the uploaded file
    setActiveTab('browse');
    
    // Auto-confirm: for units (non-admin), confirm immediately; 
    // for admin full form, just pre-select so user can click confirm
    if (onSelectMedia) {
      if (!showFullForm) {
        // Simple mode: auto-confirm after brief delay so user sees what was selected
        setTimeout(() => {
          onSelectMedia(newMedia);
        }, 800);
      }
      // In showFullForm mode, user sees the selection preview and clicks "Insert" manually
    }
  };

  // Handle media type change (for 'both' mode)
  const handleMediaTypeChange = (newMediaType) => {
    setCurrentMediaType(newMediaType);
    setSelectedMedia(null); // Clear selection when switching types
    setActiveTab('upload'); // Reset to upload tab
    setError(null);
  };

  const mediaLabel = isImage ? 'Image' : 'Audio';
  const browserLabel = isImage ? 'Image Library' : 'Audio Library';

  return (
    <div className="media-selector" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxHeight: '85vh',
      overflow: 'hidden'
    }}>
      <div className="selector-header" style={{
        padding: '15px 20px',
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0
      }}>
        <h3 style={{ margin: 0 }}>
          {showMediaTypeTabs ? 'Media Library' : `Select ${mediaLabel}`}
        </h3>
        {error && <div className="error-message" style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '8px 12px',
          borderRadius: '4px',
          marginTop: '10px',
          fontSize: '14px'
        }}>{error}</div>}
      </div>

      {/* MEDIA TYPE TABS (for 'both' mode) */}
      {showMediaTypeTabs && (
        <div style={{
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            gap: '0'
          }}>
            <button 
              onClick={() => handleMediaTypeChange('image')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: currentMediaType === 'image' ? '#2196f3' : '#666',
                borderBottom: currentMediaType === 'image' ? '2px solid #2196f3' : '2px solid transparent',
                fontWeight: '500'
              }}
            >
              📷 Images
            </button>
            <button 
              onClick={() => handleMediaTypeChange('audio')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: currentMediaType === 'audio' ? '#2196f3' : '#666',
                borderBottom: currentMediaType === 'audio' ? '2px solid #2196f3' : '2px solid transparent',
                fontWeight: '500'
              }}
            >
              🎵 Audio
            </button>
          </div>
        </div>
      )}

      {/* UPLOAD/BROWSE TABS */}
      <div className="selector-tabs" style={{
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0
      }}>
        <div className="tab-buttons" style={{
          display: 'flex',
          gap: '0'
        }}>
          <button 
            className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              color: activeTab === 'upload' ? '#2196f3' : '#666',
              borderBottom: activeTab === 'upload' ? '2px solid #2196f3' : '2px solid transparent',
              fontWeight: '500'
            }}
          >
            ⬆️ Upload New {mediaLabel}
          </button>
          <button 
            className={`tab-button ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              color: activeTab === 'browse' ? '#2196f3' : '#666',
              borderBottom: activeTab === 'browse' ? '2px solid #2196f3' : '2px solid transparent',
              fontWeight: '500'
            }}
          >
            📂 {browserLabel}
          </button>
        </div>
      </div>

      <div className="tab-content" style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {activeTab === 'upload' && (
          <div className="upload-tab" style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px'
          }}>
            {isImage ? (
              <MediaUpload 
                onUploadComplete={handleUploadComplete}
                showFullForm={showFullForm}
                unitId={unitId}
              />
            ) : (
              <AudioUpload 
                onUploadComplete={handleUploadComplete}
                showFullForm={showFullForm}
                unitId={unitId}
              />
            )}
          </div>
        )}

        {activeTab === 'browse' && (
          <div className="browse-tab" style={{
            flex: 1,
            overflow: 'hidden',
            padding: '20px'
          }}>
            {isImage ? (
              <MediaBrowser
                onSelectMedia={handleMediaSelect}
                selectedMediaId={selectedMedia?.id}
                unitId={unitId}
                showSearchBar={true}
                showCategoryFilter={showFullForm} // Only show in admin mode
              />
            ) : (
              <AudioBrowser
                onSelectAudio={handleMediaSelect}
                onUploadComplete={handleUploadComplete}
                selectedAudioId={selectedMedia?.id}
                unitId={unitId}
                showSearchBar={true}
              />
            )}
          </div>
        )}
      </div>

      {selectedMedia && (
        <div className="selection-preview" style={{
          padding: '15px 20px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9',
          flexShrink: 0
        }}>
          <div className="preview-label" style={{
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '10px',
            color: '#333'
          }}>
            Selected {mediaLabel.toLowerCase()}:
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            {isImage ? (
              <img 
                src={selectedMedia.url} 
                alt={selectedMedia.altText || selectedMedia.filename} 
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            ) : (
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#2196f3',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px'
              }}>
                🎵
              </div>
            )}
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {selectedMedia.filename || selectedMedia.name || selectedMedia.title}
              </div>
              {selectedMedia.dimensions && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedMedia.dimensions}
                </div>
              )}
              {selectedMedia.size && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {(selectedMedia.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="selector-actions" style={{
        padding: '15px 20px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        flexShrink: 0,
        backgroundColor: '#fff'
      }}>
        <button 
          onClick={onCancel}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
        <button 
          onClick={handleConfirmSelection}
          disabled={!selectedMedia}
          style={{
            padding: '8px 16px',
            backgroundColor: selectedMedia ? '#4caf50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedMedia ? 'pointer' : 'not-allowed',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Insert Selected {mediaLabel}
        </button>
      </div>
    </div>
  );
};

export default MediaSelector;